<template>
  <div class="additional-seats">
    <div
      v-if="!Object.keys(mapped_decks).length"
      class="additional-seats__empty"
    >
      Selezione del posto non disponibile per questa prenotazione.
    </div>
    <div v-else class="additional-seats__frame">
      <seat-map
        :mustSelectSeats="must_select_seats"
        :mappedDecks="mapped_decks"
        :passengers="passengers"
        :selections="serialized_selections"
        :handleSeatSelection="
          (segmentId, passengerId, seat) =>
            handle_seat_selection(segmentId, passengerId, seat)
        "
        :resetSeatSelection="() => reset_seat_selection()"
        :updateParentWithCurrentStatus="(data) => $emit('status', data)"
        :resetSegmentSelections="
          (segmentId) => reset_segment_selections(segmentId)
        "
        @tracked_event="track_event"
      />
    </div>

    <vb-modal
      :vb_options="{
        id: 'additional-seats-fail-modal',
        title: 'Posto non selezionabile',
        ok_only: true,
        centered: true,
        ok_variant: 'info',
      }"
    >
      {{ modal_description }}
    </vb-modal>
  </div>
</template>

<script>
import { read_seats, seats_are_mandatory } from "@liveflight/providers";
import SeatMap from "@liveflight/components/blocks/seat-map/seat-map.vue";
import {
  slim_seat,
  slim_selections,
  slim_traveler,
  slim_flight_offer,
} from "@liveflight/utilities/rybbit";
import {
  apply_seat_selection,
  check_seats_valid_for_children,
  filterHeldPassengers,
  find_passenger_on_seat,
  isSeatAvailableForPassenger,
  remove_seat_selection,
} from "@liveflight/utilities/seat-map";
import { is_adult, is_child, is_teen, is_infant } from "@liveflight/utilities/travelers";

const TRAVELER_PREDICATES = { ADULT: is_adult, TEEN: is_teen, CHILD: is_child };

export default {
  name: "additional-seats",
  components: {
    "seat-map": SeatMap,
  },
  props: {
    flight_offer: {
      type: Object,
      required: true,
    },
    seatmap: {
      type: Object,
      default: () => ({}),
    },
    travelers: {
      type: Array,
      default: () => [],
    },
    value: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      modal_description: "",
      sitting_passengers: [],
      travelers_by_age: {
        ADULT: { count: 0, passengers: {} },
        CHILD: { count: 0, passengers: {} },
      },
      travelers_map: {},
      seat_chains: {},
    };
  },
  computed: {
    mapped_decks() {
      return (this.seatmap && this.seatmap.mappedDecks) || {};
    },
    seat_segments() {
      return (
        (this.seatmap &&
          this.seatmap.data &&
          this.seatmap.data.seatmaps &&
          this.seatmap.data.seatmaps.data) ||
        []
      );
    },
    held_passengers() {
      const held = {};
      ((this.flight_offer && this.flight_offer.travelerPricings) || [])
        .filter(is_infant)
        .forEach((i) => {
          held[i.associatedAdultId] = i;
        });
      return held;
    },
    passengers() {
      return (this.travelers || []).map((p) => {
        const heldByPassengerId = this.held_passengers[p.id];
        return !heldByPassengerId ? p : { ...p, holding: heldByPassengerId };
      });
    },
    parsed_seat_selections: {
      get() {
        return this.value || {};
      },
      set(value) {
        this.$emit("input", value);
      },
    },
    serialized_selections() {
      try {
        return JSON.stringify(this.parsed_seat_selections || {});
      } catch (e) {
        return "{}";
      }
    },
    must_select_seats() {
      return seats_are_mandatory(this.flight_offer);
    },
    /**
     * Pure completeness check — every sitting passenger has a seat on
     * every flight segment. Callers combine with must_select_seats (or
     * any other policy) to gate their UI; this never blocks on its own.
     */
    all_seats_selected() {
      const { itineraries = [] } = this.flight_offer || {};
      return itineraries.every((itinerary) => {
        const { segments = [] } = itinerary || {};
        return segments.every((segment) => {
          const { id = "" } = segment || {};
          const segment_selections = this.parsed_seat_selections[id] || {};
          return (
            Object.keys(segment_selections).length >=
            this.sitting_passengers.length
          );
        });
      });
    },
    flight_offer_rybbit_data() {
      return slim_flight_offer(this.flight_offer);
    },
  },
  created() {
    this.rebuild_traveler_caches();
  },
  watch: {
    seatmap: {
      handler(next) {
        if (next && Object.keys(next.mappedDecks || {}).length) {
          this.hydrate_existing_selections();
        }
      },
      deep: true,
    },
    all_seats_selected: {
      handler(value) {
        this.$emit("seats_complete", !!value);
      },
      immediate: true,
    },
    travelers() {
      this.rebuild_traveler_caches();
    },
  },
  mounted() {
    if (this.must_select_seats) {
      this.track_event({
        event: "seat-map:mandatory-selection",
        data: { type: "info" },
      });
    }
    this.hydrate_existing_selections();
  },
  methods: {
    isSeatAvailableForPassenger,
    rebuild_traveler_caches() {
      this.sitting_passengers = filterHeldPassengers(this.passengers);
      const travelers_by_age = {};
      let travelers_map = {};
      Object.keys(TRAVELER_PREDICATES).forEach((age) => {
        const passengers_by_age = this.passengers.filter(TRAVELER_PREDICATES[age]);
        const passengers = Object.fromEntries(passengers_by_age.map((p) => [p.id, p]));
        travelers_by_age[age] = { count: passengers_by_age.length, passengers };
        travelers_map = { ...travelers_map, ...passengers };
      });
      this.travelers_by_age = travelers_by_age;
      this.travelers_map = travelers_map;
    },
    /**
     * Combine three inputs into the seat-map's `value` shape:
     *   1. read_seats() walks the canonical offer's per-pax-segment
     *      additionalServices, returning {[travelerId]: SeatBlock}.
     *   2. partial entries seeded at retrieve time
     *      ({type:'seat', seatNumber, ...}) — enriched with the full
     *      SeatBlock so the UI can render row/column/pricingByTraveler.
     *   3. user-applied selections already carrying full metadata
     *      (apply_seat_selection wrote them) — kept as-is.
     */
    hydrate_existing_selections() {
      if (!this.seat_segments.length) return;
      const current = this.value || {};
      const next = this.seat_segments.reduce((acc, segment) => {
        const { segmentId } = segment;
        if (!segmentId) return acc;
        const segment_seats = (segment.decks || []).flatMap(
          (deck) => deck.seats || [],
        );
        const seat_by_number = {};
        segment_seats.forEach((seat) => {
          if (seat && seat.number) seat_by_number[seat.number] = seat;
        });
        // Start from offer-committed seats (full SeatBlock from the
        // seatmap), then overlay whatever the page-side `value` has.
        const per_pax = {
          ...read_seats(this.flight_offer, segmentId, segment_seats),
        };
        const existing_per_pax = current[segmentId] || {};
        Object.keys(existing_per_pax).forEach((traveler_id) => {
          const entry = existing_per_pax[traveler_id] || {};
          const has_metadata =
            entry.row !== undefined && entry.pricingByTraveler;
          if (has_metadata) {
            per_pax[traveler_id] = entry;
            return;
          }
          const seat_number =
            entry.seatNumber ||
            entry.chargeableSeatNumber ||
            entry.number ||
            "";
          const enriched = seat_number ? seat_by_number[seat_number] : null;
          per_pax[traveler_id] = enriched || entry;
        });
        if (Object.keys(per_pax).length) acc[segmentId] = per_pax;
        return acc;
      }, {});
      this.parsed_seat_selections = next;
    },
    reset_segment_selections(segmentId) {
      if (this.parsed_seat_selections[segmentId]) {
        const next = { ...this.parsed_seat_selections };
        delete next[segmentId];
        this.parsed_seat_selections = next;
      }
    },
    reset_seat_selection() {
      this.parsed_seat_selections = {};
    },
    is_passenger_holding_infant(passengerId) {
      return !!this.held_passengers[passengerId];
    },
    fail_selection_with_reason(params) {
      const { message, traveler_id } = params;
      this.track_event({
        event: "seat-map:selection:fail",
        data: { type: "warning", message, traveler_id },
      });
      this.modal_description = message;
      this.$bvModal.show("additional-seats-fail-modal");
      return { success: false };
    },
    handle_seat_selection(segmentId, traveler_id, seat) {
      if (!segmentId || !traveler_id || !seat) return { success: false };
      try {
        const segment_selections = this.parsed_seat_selections[segmentId] || {};

        const passenger_on_current_seat = find_passenger_on_seat(
          segment_selections,
          seat.number,
        );
        if (passenger_on_current_seat) {
          this.parsed_seat_selections = remove_seat_selection(
            this.parsed_seat_selections,
            segmentId,
            passenger_on_current_seat,
          );
          return { success: true, displacedOwnerId: passenger_on_current_seat };
        }

        if (!this.isSeatAvailableForPassenger(traveler_id, seat)) {
          return this.fail_selection_with_reason({
            message: "Il posto selezionato non è più disponibile.",
            traveler_id,
            seat,
          });
        }

        if (
          this.is_passenger_holding_infant(traveler_id) &&
          !seat.isSuitableForInfant
        ) {
          return this.fail_selection_with_reason({
            message:
              "Questo posto non va bene per chi viaggia con un neonato in braccio. Dai un'occhiata alla legenda e scegli un posto adatto.",
            traveler_id,
            seat,
          });
        }

        if (this.travelers_by_age["CHILD"].count) {
          const { valid, seatChains } = check_seats_valid_for_children({
            segment_id: segmentId,
            passenger_id: traveler_id,
            seat,
            parsed_seat_selections: this.parsed_seat_selections,
            travelers_by_age: this.travelers_by_age,
          });
          if (!valid) {
            return this.fail_selection_with_reason({
              message:
                "Ops! I bambini sotto gli 11 anni non possono sedersi da soli. Mettili vicino a un adulto.",
              traveler_id,
              seat,
            });
          }
          this.$set(this.seat_chains, segmentId, { ...seatChains });
        }

        this.parsed_seat_selections = apply_seat_selection(
          this.parsed_seat_selections,
          segmentId,
          traveler_id,
          seat,
        );
        this.track_event({
          event: "seat-map:selection:select-seat",
          data: { type: "info", traveler_id, seat },
        });
        return { success: true };
      } catch (e) {
        this.track_event({
          event: "seat-map:selection:fail",
          data: { type: "warning", message: e.message, traveler_id, seat },
        });
        return { success: false };
      }
    },
    track_event(params) {
      const { event, data = {} } = params;
      const enriched = { ...data };
      if (data.seat) {
        enriched.seat = slim_seat(data.seat);
      }
      if (data.traveler_id && this.travelers_map[data.traveler_id]) {
        enriched.traveler = slim_traveler(this.travelers_map[data.traveler_id]);
      }
      this.track_form({
        event,
        data: enriched,
        context: {
          flightOffer: this.flight_offer_rybbit_data,
          selections: slim_selections(this.parsed_seat_selections),
        },
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.additional-seats {
  &__frame {
    border: 1px solid var(--light-gray);
    border-radius: $border-radius-lg * 2;
    padding-top: 0;
  }

  &__empty {
    background: $white;
    border-radius: 5px;
    padding: $grid-gutter-width;
    text-align: center;
    color: $gray-700;
  }
}
</style>
